import React, { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Chip,
} from '@mui/material'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { Bar } from 'react-chartjs-2'
import CardTitle from 'src/components/sharedComponents/CardTitle'
import {
  getRolUser,
  getModelos,
  getEscenariosCompra,
  getEscenariosCompraEnFirme,
  getGraficoDiferencialSellIn,
} from '@auteco/services/motosSnopServices'
import { toast } from 'react-toastify'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
)

const COMPANIAS = ['TVS', 'VICTORY+', 'ELECTRIC', 'BLUE']
const TIPOS_PERIODO = [
  { value: 'mes', label: 'Mes' },
  { value: 'semana', label: 'Semana' },
]

const MESES_NOMBRE = {
  1: 'Enero', 2: 'Febrero', 3: 'Marzo', 4: 'Abril',
  5: 'Mayo', 6: 'Junio', 7: 'Julio', 8: 'Agosto',
  9: 'Septiembre', 10: 'Octubre', 11: 'Noviembre', 12: 'Diciembre',
}

const generarAnios = () => {
  const anioActual = new Date().getFullYear()
  return [anioActual - 1, anioActual, anioActual + 1]
}

/** Normaliza la respuesta de /escenarios_con_compra_en_firme a { value, label }[]. */
const extraerOpcionesEscenarioCompraEnFirme = (raw) => {
  if (!raw) return []
  let list
  if (Array.isArray(raw)) list = raw
  else if (Array.isArray(raw.escenarios)) list = raw.escenarios
  else if (Array.isArray(raw.data)) list = raw.data
  else return []
  return list.map((item, idx) => {
    if (typeof item === 'string' || typeof item === 'number') {
      const v = String(item)
      return { value: v, label: v }
    }
    const value =
      item.escenario_compra_en_firme ??
      item.escenario ??
      item.nombre ??
      item.id
    const label = item.etiqueta ?? item.descripcion ?? item.nombre ?? value ?? idx
    const valueStr = value != null ? String(value) : String(idx)
    return { value: valueStr, label: String(label) }
  })
}

const sellInItemGrafico = (item) =>
  item.sell_in ?? item.sell_in_comparativo ?? 0

/** Escala Y solo según diferenciales (no sell_in ni sell_in_base). */
const rangoEjeYDiferencial = (valores) => {
  if (!valores.length) return { yMin: 0, yMax: 10 }
  const minD = Math.min(...valores)
  const maxD = Math.max(...valores)
  const rango = maxD - minD

  if (minD === 0 && maxD === 0) {
    return { yMin: 0, yMax: 10 }
  }

  const padRel = rango === 0 ? Math.max(Math.abs(minD), Math.abs(maxD), 1) * 0.12 : rango * 0.12
  const padMin = Math.max(padRel, 1)

  if (minD >= 0) {
    return { yMin: 0, yMax: maxD + padMin }
  }
  if (maxD <= 0) {
    return { yMin: minD - padMin, yMax: 0 }
  }
  return { yMin: minD - padMin, yMax: maxD + padMin }
}

const arrowPlugin = {
  id: 'arrowAnnotation',
  afterDraw(chart) {
    const { ctx, chartArea, scales } = chart
    const meta = chart.options.plugins.arrowAnnotation
    if (!meta?.targetIndex || meta.targetIndex < 0) return

    const x = scales.x.getPixelForValue(meta.targetIndex)
    const yTop = chartArea.top - 10
    const arrowLen = 30
    const headLen = 10

    ctx.save()
    ctx.strokeStyle = '#334155'
    ctx.fillStyle = '#334155'
    ctx.lineWidth = 3

    ctx.beginPath()
    ctx.moveTo(x, yTop - arrowLen)
    ctx.lineTo(x, yTop)
    ctx.stroke()

    ctx.beginPath()
    ctx.moveTo(x - headLen, yTop - headLen)
    ctx.lineTo(x, yTop + 2)
    ctx.lineTo(x + headLen, yTop - headLen)
    ctx.fill()

    if (meta.label) {
      ctx.font = 'bold 11px Inter, Roboto, sans-serif'
      ctx.textAlign = 'center'
      ctx.fillStyle = '#64748b'
      ctx.fillText(meta.label, x, yTop - arrowLen - 8)
    }
    ctx.restore()
  },
}

ChartJS.register(arrowPlugin)

const GraficoSellInContainer = () => {
  const [infoBars, setInfoBars] = useState({})
  const [compania, setCompania] = useState('TVS')
  const [escenarios, setEscenarios] = useState([])
  const [escenarioBase, setEscenarioBase] = useState('')
  const [modelos, setModelos] = useState([])
  const [modeloSeleccionado, setModeloSeleccionado] = useState('')
  const [tipoPeriodo, setTipoPeriodo] = useState('mes')
  const [anioBase, setAnioBase] = useState(new Date().getFullYear())
  const [opcionesEscenarioComparativo, setOpcionesEscenarioComparativo] = useState([])
  const [escenarioComparativo, setEscenarioComparativo] = useState('')
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(null)

  useEffect(() => {
    const fetchRol = async () => {
      const response = await getRolUser()
      if (response) setInfoBars(response.iam.nombre)
    }
    fetchRol()
  }, [])

  useEffect(() => {
    const fetchEscenarios = async () => {
      const response = await getEscenariosCompra(compania)
      if (response?.escenarios) {
        setEscenarios(response.escenarios)
        if (response.escenarios.length > 0) {
          setEscenarioBase(response.escenarios[0].escenario)
        }
      } else {
        setEscenarios([])
        setEscenarioBase('')
      }
    }
    fetchEscenarios()
  }, [compania])

  useEffect(() => {
    const fetchModelos = async () => {
      const response = await getModelos(compania)
      if (response?.modelos) {
        setModelos(response.modelos)
        if (response.modelos.length > 0) {
          setModeloSeleccionado(response.modelos[0].modelo_planeacion)
        }
      } else {
        setModelos([])
        setModeloSeleccionado('')
      }
    }
    fetchModelos()
  }, [compania])

  useEffect(() => {
    const fetchEscenariosFirme = async () => {
      if (!modeloSeleccionado) {
        setOpcionesEscenarioComparativo([])
        setEscenarioComparativo('')
        return
      }
      const response = await getEscenariosCompraEnFirme(modeloSeleccionado, tipoPeriodo)
      const opciones = extraerOpcionesEscenarioCompraEnFirme(response)
      setOpcionesEscenarioComparativo(opciones)
      if (opciones.length > 0) {
        setEscenarioComparativo(opciones[0].value)
      } else {
        setEscenarioComparativo('')
      }
    }
    fetchEscenariosFirme()
  }, [modeloSeleccionado, tipoPeriodo])

  const handleConsultar = useCallback(async () => {
    if (!modeloSeleccionado || !escenarioBase) {
      toast.warn('Selecciona un modelo y un escenario base')
      return
    }
    if (!escenarioComparativo) {
      toast.warn('Selecciona un escenario comparativo')
      return
    }
    setLoading(true)
    try {
      const response = await getGraficoDiferencialSellIn(
        modeloSeleccionado,
        escenarioBase,
        escenarioComparativo,
        tipoPeriodo,
        anioBase,
      )
      if (response) {
        setData(response)
      } else {
        toast.error('No se obtuvieron datos')
        setData(null)
      }
    } catch {
      toast.error('Error al consultar el gráfico')
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [modeloSeleccionado, escenarioBase, escenarioComparativo, tipoPeriodo, anioBase])

  const chartConfig = useMemo(() => {
    const grafico = data?.meses ?? data?.grafico
    if (!grafico || grafico.length === 0) return null
    const sellInBase =
      data.sell_in_base ?? grafico[0]?.sell_in_base ?? 0

    const labels = grafico.map(item => {
      if (tipoPeriodo === 'mes') {
        const nombre = MESES_NOMBRE[item.mes_semana] || item.mes_semana
        return grafico.filter(g => g.mes_semana === item.mes_semana).length > 1
          || grafico[0].anio !== grafico[grafico.length - 1].anio
          ? `${nombre} ${item.anio}`
          : nombre
      }
      return `Sem ${item.mes_semana} (${item.anio})`
    })

    const valores = grafico.map(item => item.diferencial)

    const primerCambioIdx = grafico.findIndex(item => item.diferencial !== 0)

    const barColors = valores.map(v =>
      v !== 0 ? 'rgba(55, 65, 81, 0.88)' : 'rgba(203, 213, 225, 0.4)',
    )
    const borderColors = valores.map(v =>
      v !== 0 ? '#1e293b' : '#cbd5e1',
    )

    const hoy = new Date()
    const mesActual = hoy.getMonth() + 1
    const anioActual = hoy.getFullYear()
    let arrowIndex = -1
    if (tipoPeriodo === 'mes') {
      arrowIndex = grafico.findIndex(
        item => item.mes_semana === mesActual && item.anio === anioActual,
      )
    }
    const fechaHoy = hoy.toISOString().split('T')[0]

    const { yMin, yMax } = rangoEjeYDiferencial(valores)

    const chartData = {
      labels,
      datasets: [
        {
          label: 'Diferencial Sell In',
          data: valores,
          backgroundColor: barColors,
          borderColor: borderColors,
          borderWidth: 1,
          borderRadius: 5,
          barPercentage: 0.55,
          categoryPercentage: 0.75,
        },
      ],
    }

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: { top: 48, bottom: 36, left: 12, right: 12 },
      },
      plugins: {
        legend: { display: false },
        title: {
          display: true,
          text: `Diferencial Sell In — ${modeloSeleccionado}`,
          font: { size: 16, weight: 'bold', family: 'Inter, Roboto, sans-serif' },
          color: '#1e293b',
          padding: { bottom: 24 },
        },
        tooltip: {
          backgroundColor: '#1e293b',
          titleFont: { size: 13, weight: 'bold' },
          bodyFont: { size: 12 },
          cornerRadius: 8,
          padding: 12,
          callbacks: {
            title(ctx) {
              const idx = ctx[0].dataIndex
              const item = grafico[idx]
              const periodo = tipoPeriodo === 'mes'
                ? `${MESES_NOMBRE[item.mes_semana]} ${item.anio}`
                : `Semana ${item.mes_semana} (${item.anio})`
              return periodo
            },
            label(ctx) {
              const idx = ctx.dataIndex
              const item = grafico[idx]
              const lines = [` Diferencial: ${item.diferencial.toLocaleString()}`]
              if (item.sell_in_base != null) {
                lines.push(` Sell In base: ${Number(item.sell_in_base).toLocaleString()}`)
              }
              if (item.sell_in_comparativo != null) {
                lines.push(` Sell In comparativo: ${Number(item.sell_in_comparativo).toLocaleString()}`)
              }
              if (lines.length === 1) {
                lines.push(` Sell In: ${sellInItemGrafico(item).toLocaleString()}`)
              }
              return lines
            },
          },
        },
        datalabels: {
          display(ctx) {
            return ctx.dataset.data[ctx.dataIndex] !== 0
          },
          clip: false,
          anchor: 'end',
          align(ctx) {
            const v = ctx.dataset.data[ctx.dataIndex]
            return v >= 0 ? 'end' : 'start'
          },
          offset: 4,
          color: '#0f172a',
          backgroundColor: 'rgba(255,255,255,0.92)',
          borderRadius: 4,
          padding: { top: 2, right: 5, bottom: 2, left: 5 },
          font: { weight: 'bold', size: 11 },
          formatter(value) {
            return value?.toLocaleString() ?? ''
          },
        },
        arrowAnnotation: {
          targetIndex: arrowIndex,
          label: fechaHoy,
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            color: '#475569',
            font: { size: 11, weight: '500' },
            maxRotation: 45,
            minRotation: 0,
          },
        },
        y: {
          min: yMin,
          max: yMax,
          grid: { color: '#e2e8f0', lineWidth: 1 },
          ticks: {
            color: '#64748b',
            font: { size: 11 },
            callback(value) {
              return value.toLocaleString()
            },
          },
          border: { display: false },
        },
      },
    }

    return { chartData, options, sellInBase, primerCambioIdx }
  }, [data, modeloSeleccionado, tipoPeriodo])

  if (infoBars !== 'Comprador') {
    return (
      <Box sx={{ padding: 3 }}>
        <CardTitle title="Gráfico Sell In" />
        <Typography>No tienes permisos para acceder a esta página</Typography>
      </Box>
    )
  }

  const serieGrafico = data?.meses ?? data?.grafico

  return (
    <Box sx={{ padding: 3 }}>
      <CardTitle title="Gráfico Sell In" />

      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Compañía</InputLabel>
            <Select
              value={compania}
              label="Compañía"
              onChange={e => {
                setCompania(e.target.value)
                setData(null)
              }}
            >
              {COMPANIAS.map(c => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Escenario base</InputLabel>
            <Select
              value={escenarioBase}
              label="Escenario base"
              onChange={e => setEscenarioBase(e.target.value)}
            >
              {escenarios.map(esc => (
                <MenuItem key={esc.escenario} value={esc.escenario}>
                  {esc.escenario}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Modelo</InputLabel>
            <Select
              value={modeloSeleccionado}
              label="Modelo"
              onChange={e => setModeloSeleccionado(e.target.value)}
            >
              {modelos.map(m => (
                <MenuItem key={m.modelo_planeacion} value={m.modelo_planeacion}>
                  {m.modelo_planeacion}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small"  style={{ display: 'none' }} sx={{ minWidth: 120 }}>
            <InputLabel>Período</InputLabel>
            <Select
              value={tipoPeriodo}
              label="Período"
              onChange={e => {
                setTipoPeriodo(e.target.value)
              }}
            >
              {TIPOS_PERIODO.map(tp => (
                <MenuItem key={tp.value} value={tp.value}>{tp.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Año base</InputLabel>
            <Select
              value={anioBase}
              label="Año base"
              onChange={e => setAnioBase(e.target.value)}
            >
              {generarAnios().map(a => (
                <MenuItem key={a} value={a}>{a}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Escenario comparativo</InputLabel>
            <Select
              value={escenarioComparativo}
              label="Escenario comparativo"
              onChange={e => setEscenarioComparativo(e.target.value)}
            >
              {opcionesEscenarioComparativo.length === 0 ? (
                <MenuItem value="" disabled>
                  {modeloSeleccionado ? 'Sin opciones' : 'Selecciona modelo'}
                </MenuItem>
              ) : (
                opcionesEscenarioComparativo.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            onClick={handleConsultar}
            disabled={
              loading ||
              !modeloSeleccionado ||
              !escenarioBase ||
              !escenarioComparativo
            }
            sx={{
              height: 40,
              px: 4,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              backgroundColor: '#334155',
              '&:hover': { backgroundColor: '#1e293b' },
            }}
          >
            {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Consultar'}
          </Button>
        </Box>
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={48} sx={{ color: '#334155' }} />
        </Box>
      )}

      {!loading && chartConfig && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: '1px solid #e2e8f0',
          }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2.5 }}>
            {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                Sell In base:
              </Typography>
              <Chip
                label={chartConfig.sellInBase.toLocaleString()}
                size="small"
                sx={{
                  backgroundColor: '#334155',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                }}
              />
            </Box> */}

            {/* {chartConfig.primerCambioIdx >= 0 && serieGrafico && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                  Primer cambio en:
                </Typography>
                <Chip
                  label={
                    tipoPeriodo === 'mes'
                      ? `${MESES_NOMBRE[serieGrafico[chartConfig.primerCambioIdx].mes_semana]} ${serieGrafico[chartConfig.primerCambioIdx].anio}`
                      : `Semana ${serieGrafico[chartConfig.primerCambioIdx].mes_semana} (${serieGrafico[chartConfig.primerCambioIdx].anio})`
                  }
                  size="small"
                  sx={{
                    backgroundColor: '#059669',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                  }}
                />
              </Box>
            )} */}
          </Box>

          <Box sx={{ height: 420, width: '100%' }}>
            <Bar data={chartConfig.chartData} options={chartConfig.options} />
          </Box>
        </Paper>
      )}

      {!loading && !data && (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            borderRadius: 3,
            border: '1px dashed #cbd5e1',
            textAlign: 'center',
          }}
        >
          <Typography variant="h6" sx={{ color: '#94a3b8', fontWeight: 500 }}>
            Selecciona los filtros y presiona Consultar
          </Typography>
          <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 1 }}>
            Se mostrará el gráfico diferencial de Sell In
          </Typography>
        </Paper>
      )}
    </Box>
  )
}

export default GraficoSellInContainer
